/**
 * Turno semanal A/B (Wed→Tue weekly rotation).
 *
 * Reference: 2026-04-29 (Wednesday) is the first day of a Turno A week.
 * Each subsequent week alternates B, A, B, A...
 *
 * Used by the weekly calendar view (Mié→Mar partition) to label each
 * week and to align operators (Usuario.turno = 'A' | 'B') with their
 * scheduled tickets.
 *
 * All computations are UTC-only. Local timezone is irrelevant — the
 * calendar week is anchored on Wednesday, period.
 */

/** Reference date: 2026-04-29 (Wed) starts a Turno A week. */
const TURNO_REFERENCE_MS = Date.UTC(2026, 3, 29);

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MS_PER_WEEK = 7 * MS_PER_DAY;

/**
 * Returns the UTC midnight of the most recent Wednesday on or before `date`.
 * Equivalent to "start of this week's Wed→Tue range".
 */
export function startOfTurnoWeek(date: Date | string): Date {
  const d = typeof date === 'string' ? new Date(`${date.slice(0, 10)}T00:00:00Z`) : date;
  const utc = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const day = utc.getUTCDay(); // Sun=0, Mon=1, ..., Wed=3, ..., Sat=6
  const offsetToWed = (day - 3 + 7) % 7;
  utc.setUTCDate(utc.getUTCDate() - offsetToWed);
  return utc;
}

/**
 * Returns the UTC midnight Tuesday that ends the week containing `date`.
 * (Wed start + 6 days → Tue inclusive end.)
 */
export function endOfTurnoWeek(date: Date | string): Date {
  const start = startOfTurnoWeek(date);
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 6);
  return end;
}

/**
 * Returns half-open bounds [start, end) for the Turno week containing `date`.
 * `start` = Wednesday 00:00 UTC, `end` = next Wednesday 00:00 UTC (exclusive).
 * Use these directly in Prisma `gte`/`lt` filters.
 */
export function getTurnoWeekBounds(date: Date | string): { start: Date; end: Date } {
  const start = startOfTurnoWeek(date);
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 7);
  return { start, end };
}

/**
 * Returns the Turno label ('A' or 'B') for the week containing `date`.
 * Reference: 2026-04-29 (Wed) → 'A'. Subsequent weeks alternate B, A, B, ...
 */
export function getTurnoForDate(date: Date | string): 'A' | 'B' {
  const wedOfWeek = startOfTurnoWeek(date);
  const diffMs = wedOfWeek.getTime() - TURNO_REFERENCE_MS;
  const diffWeeks = Math.round(diffMs / MS_PER_WEEK);
  // Modulo that handles negative values correctly
  const parity = ((diffWeeks % 2) + 2) % 2;
  return parity === 0 ? 'A' : 'B';
}

/**
 * Resolves the EFFECTIVE turno label for a ticket — the single source of truth
 * for a ticket's shift across execution, the líder mini-app, the cuadrilla
 * picker and the mantenedor list.
 *
 * The turno is DERIVED from the scheduled date (`getTurnoForDate`) instead of
 * being read from a stored column, with two intentional exceptions:
 *
 *  1. An explicit special shift stored on the ticket (any value other than the
 *     'A'/'B' rotation — e.g. legacy 'H'/'DTH') is honored as an override.
 *  2. A closed ticket (`estadoOt === 'APROBADO'`) keeps its historically-stored
 *     turno when present, so already-signed tickets are never re-labeled
 *     retroactively. New tickets carry no stored turno (`null`) and therefore
 *     derive from the date even once closed.
 *
 * Open / in-flight tickets always follow their scheduled date — so rescheduling
 * (via the UI or directly in the DB) is reflected automatically, with no stored
 * value left to desync.
 *
 * @param estadoOt        Ticket state, e.g. 'INICIADO' | 'APROBADO'.
 * @param fechaProgramada Scheduled date (Date | 'YYYY-MM-DD' | null).
 * @param storedTurno     Turno name stored on the ticket (`ot.turno?.nombre`), or null.
 */
export function resolveTicketTurno(input: {
  estadoOt: string;
  fechaProgramada: Date | string | null;
  storedTurno: string | null;
}): string | null {
  const { estadoOt, fechaProgramada, storedTurno } = input;

  // 1. Honor explicit special shifts (anything outside the A/B rotation).
  if (storedTurno && storedTurno !== 'A' && storedTurno !== 'B') {
    return storedTurno;
  }

  // 2. Closed (signed) tickets keep their historical turno; new tickets with no
  //    stored turno still derive from the date.
  if (estadoOt === 'APROBADO') {
    return storedTurno ?? (fechaProgramada ? getTurnoForDate(fechaProgramada) : null);
  }

  // 3. Open / in-flight: the scheduled date is the single source of truth.
  if (fechaProgramada) {
    return getTurnoForDate(fechaProgramada);
  }

  // 4. No date to derive from: fall back to whatever was stored (may be null).
  return storedTurno;
}

/**
 * Formats a Date as YYYY-MM-DD using UTC components.
 */
export function toUtcDateString(date: Date): string {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
